import { Dialog } from "@radix-ui/react-dialog";
import { DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogClose } from "components/ui/dialog";
import { Button } from "components/ui/button";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "hooks/use-toast";
import { Check, Copy, Loader2 } from "lucide-react";
import { useChatStore } from "state/store/chat/use-chat-store";
import { InvitationPayload } from "features/group-chat/services/group-services";
import { useModelStore } from "state/store/chat/use-model-store";
import { useGetGroupChatShareToken, useInviteAssistant } from "features/group-chat/hooks/use-invite";
import { useNavigate } from "react-router-dom";
import { DEFAULT_CONVERSATION_NAME } from "utils/constants/chat-constants";
import { useCommonStore } from "state/store/common/useCommonStore";
import { useTheme } from "components/core/theme-provider";
import { copyTextFromInputElement } from "lib/utils";
import { TruncateTooltip } from "hooks/use-truncate-tooltip";
import { TooltipProvider } from "components/ui/tooltip";

interface GroupShareModalProps {
    // conversationId: string | null
    showModal: boolean
    setShowModal: (value: boolean) => void
    shareToken: string | null,
    setShareToken: (value: string) => void
    isLoadingConversation: boolean
}

export const GroupShareModal = ({ showModal, setShowModal, shareToken, setShareToken, isLoadingConversation }: GroupShareModalProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { toast } = useToast();
    const [copied, setCopied] = useState(false);
    const linkInputRef = useRef<HTMLInputElement>(null);
    const selectedModel = useModelStore((state) => state.selectedModel);
    const activeConversationId = useChatStore((state) => state.activeConversationId);
    const inviteAssistantMutation = useInviteAssistant();
    const getGroupChatShareTokenMutation = useGetGroupChatShareToken();
    const [isCreatingShare, setIsCreatingShare] = useState(false);
    const { theme } = useTheme();

    const generateGroupInviteLink = (token: string) => {
        const baseUrl = window.location.origin;
        const inviteLink = `${baseUrl}/group-invite/${token}`;
        return inviteLink;
    }

    const groupLink = shareToken ? generateGroupInviteLink(shareToken) : '';

    const handleStartGroupChat = async () => {
        if (!selectedModel?.id) {
            toast({
                variant: 'destructive',
                title: t('ERROR'),
                description: t('NO_MODEL_SELECTED'),
            });
            return;
        }

        let finalConversationId = activeConversationId;
        setIsCreatingShare(true);

        try {
            if (!activeConversationId) {

                const payload: InvitationPayload = {
                    invitationType: 'ADD_USER',
                    assistantId: selectedModel?.id,
                };
                const response = await inviteAssistantMutation.mutateAsync(payload);
                if (response?.conversationId) {
                    finalConversationId = response?.conversationId;

                    // Add to sidebar (appendToSidebarConversationsList handles duplicates)
                    const isoDate = new Date().toISOString();
                    const currentUser = useCommonStore.getState().user;
                    const newSidebarEntry = {
                        ConversationId: finalConversationId,
                        ConversationTitle: DEFAULT_CONVERSATION_NAME,
                        CreatedBy: currentUser?.id || '',
                        TenantId: '',
                        CreatedDate: isoDate,
                        LastUpdatedDate: isoDate,
                        Conversations: [],
                        IsGroup: true
                    };
                    useChatStore.getState().appendToSidebarConversationsList(newSidebarEntry);

                    navigate(`/chat/${finalConversationId}`);
                }
            }

            if (!finalConversationId) {
                toast({
                    variant: 'destructive',
                    title: t('ERROR'),
                    description: t('FAILED_TO_INVITE_ASSISTANT'),
                });
                return;
            }

            const shareTokenResponse = await getGroupChatShareTokenMutation.mutateAsync(finalConversationId);
            if (!shareTokenResponse?.token) {
                toast({
                    variant: 'destructive',
                    title: t('ERROR'),
                    description: t('FAILED_TO_GET_SHARE_TOKEN'),
                });
                return;
            }

            setShareToken(shareTokenResponse?.token);
            // Update the chat store with the new token
            useChatStore.getState().setConversationShareToken(finalConversationId, shareTokenResponse?.token);
        } catch (error) {
            // console.error('Failed to invite assistant:', error);
        } finally {
            setIsCreatingShare(false);
        }
    }

    const handleCopyLink = () => {
        const input = linkInputRef.current;
        if (!input?.value.trim()) {
            toast({
                variant: 'destructive',
                title: t('ERROR'),
                description: t('EMPTY_GROUP_LINK'),
            });
            return;
        }
        try {
            copyTextFromInputElement(input);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
            toast({
                variant: 'success',
                title: t('COPIED'),
                description: t('TEXT_COPY_SUCCESS'),
            });
        } catch (err) {
            console.error('Failed to copy link:', err);
            toast({
                variant: 'destructive',
                title: t('FAILED_TO_COPY_TEXT'),
                description: t('FAILED_COPY_TO_CLIPBOARD'),
            });
        }
    };

    const renderModalContent = () => {
        if (isLoadingConversation) {
            return (
                <div className="flex flex-col items-center justify-center py-12">
                    <Loader2 className="h-10 w-10 animate-spin text-gray-400" />
                    <p className="mt-4 text-sm text-gray-500">
                        {t('LOADING_CONVERSATION') || 'Loading conversation details...'}
                    </p>
                </div>
            );
        }

        if (shareToken) {
            return (
                <>
                    <DialogHeader>
                        <DialogTitle className="text-xl mb-4">
                            {t('GROUP_LINK') || 'Group link'}
                        </DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4">
                        {/* Link Input with Copy Button */}
                        <div className="relative">
                            <input
                                ref={linkInputRef}
                                key={shareToken ?? 'no-token'}
                                type="text"
                                readOnly
                                value={groupLink}
                                onFocus={(e) => e.currentTarget.select()}
                                className="h-10 rounded-3xl w-full px-4 py-3 pr-12 border border-gray-300 bg-background text-gray-700 text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent overflow-hidden"
                                aria-label={t('GROUP_SHARE_LINK') || 'Group share link'}
                            />
                            <button
                                type="button"
                                onClick={handleCopyLink}
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 hover:bg-gray-200 rounded-md transition-colors"
                                aria-label={t('COPY_LINK') || 'Copy link'}
                            >
                                {copied ? (
                                    <Check className="w-5 h-5 text-green-600" />
                                ) : (
                                    <Copy className="w-5 h-5 text-gray-600" />
                                )}
                            </button>
                        </div>

                        {/* Description */}
                        <p className="text-sm text-gray-600 leading-relaxed">
                            {t('GROUP_LINK_DESCRIPTION') ||
                                "Use a group link to invite others to join your group chat. Anyone can join your group chat with this link, and they'll be able to see the previous messages in this group chat."}
                        </p>
                    </div>

                    <DialogFooter className="mt-6 gap-2 sm:gap-0">
                        <DialogClose asChild>
                            <Button onClick={() => setShowModal(false)} variant="outline" className="flex-1 w-full sm:w-auto">
                                {t('CANCEL') || 'Cancel'}
                            </Button>
                        </DialogClose>
                        <Button
                            type="button"
                            onClick={handleCopyLink}
                            className="flex-1 w-full sm:w-auto"
                        >
                            {copied ? (t('COPIED') || 'Copied!') : (t('COPY_LINK') || 'Copy link')}
                        </Button>
                    </DialogFooter>
                </>
            );
        }

        return (
            <>

                <DialogHeader>
                    <DialogTitle className="text-lg">
                        {t('USE_APP_TOGETHER') || 'Use SeliseAI together'}
                    </DialogTitle>
                    <DialogDescription className="text-sm text-gray-500">
                        {t('GROUP_CHAT_DESCRIPTION') ||
                            'Add people to your chats to plan, share ideas, and get creative.'}
                    </DialogDescription>
                </DialogHeader>


                <DialogFooter className="gap-3 sm:gap-0 mt-16 ">
                    <DialogClose asChild>
                        <Button onClick={() => setShowModal(false)} variant="outline" className="max-w-32 min-w-32">
                            {t('CANCEL') || 'Cancel'}
                        </Button>
                    </DialogClose>
                    <Button
                        disabled={isCreatingShare}
                        onClick={handleStartGroupChat}
                        className={`max-w-40 min-w-32 flex-1 w-full sm:w-auto bg-primary-600 hover:bg-primary-700 ${theme === 'dark' ? 'text-neutral-800' : 'text-neutral-50'}`}
                    >
                        {isCreatingShare ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                {t('CREATING') || 'Creating...'}
                            </>
                        ) : (
                            <TruncateTooltip text={t('START_GROUP_CHAT') || 'Start group chat'} className="cursor-pointer" />
                        )}
                    </Button>
                </DialogFooter>
            </>
        );
    };

    return (
        <Dialog open={showModal} onOpenChange={setShowModal}>
            <DialogContent className="w-[90vw] sm:w-[464px] min-w-[350px]">
                <TooltipProvider>
                    {renderModalContent()}
                </TooltipProvider>
            </DialogContent>
        </Dialog>
    );
}
